import { useCallback, useEffect, useState } from "react";
import { useAuthStore } from "@/store/authStore";
import { MODULES, WORLDS } from "@/flow-content/curriculum";

/* ════════════════════════════════════════════════════════════════════════════
   THE BRAIN — StudoX Flow adaptive learner model
   ----------------------------------------------------------------------------
   Makes every student's path feel exclusive & personal (Duolingo-style), tuned to
   AGE and to their real LEARNING CURVE:

   • AGE DETECTION — from the backend studentProfile.age (fallback: grade → age,
     then a one-time local age pick). Age sets the starting level + filters content.
   • ABILITY (ELO per subject) — a 0–100 "Lernstufe" per world that rises when you
     ace harder content and eases when you struggle. Smarter than your age peers →
     the system automatically serves harder tasks.
   • SPACED REPETITION (SM-2-lite) — every module gets a due date; due reviews are
     resurfaced at the perfect moment so knowledge sticks.
   • RECOMMENDATION — ranks all modules per learner (due reviews ▸ optimal-challenge
     new content in the Zone of Proximal Development ▸ age-fit), so the feed is
     genuinely different for each child.

   Stored per-user in localStorage (no new sensitive server data); XP stays
   server-validated via useLearnProgress. Server-sync of the model is a future step.
   ════════════════════════════════════════════════════════════════════════════ */

const KEY = (uid) => `studox-flow-learner-v1:${uid || "anon"}`;
const DAY = 86400000;
const now = () => Date.now();

const ageNums = (s) => (String(s || "").match(/\d+/g) || []).map(Number);
/** Midpoint age a module targets. */
function moduleAgeMid(m) { const n = ageNums(m.age); return n.length ? (n[0] + (n[1] ?? n[0])) / 2 : 12; }
/** Intrinsic challenge rating 5–98 from difficulty + target age. */
export function moduleRating(m) {
  const base = { 1: 30, 2: 55, 3: 78 }[m.difficulty] ?? 50;
  const ageOff = (moduleAgeMid(m) - 12) * 2;
  return Math.max(5, Math.min(98, Math.round(base + ageOff)));
}
/** Baseline ability for a given age (8 → ~22, 12 → ~55, 16 → ~92). */
function abilityFromAge(age) { return Math.max(12, Math.min(95, Math.round((age - 7) * 11))); }

function emptyModel(age) {
  const ab = {};
  WORLDS.forEach((w) => { ab[w.id] = abilityFromAge(age); });
  return { age, ageSet: false, ability: ab, items: {}, version: 1 };
}
function read(uid, age) {
  try {
    const raw = localStorage.getItem(KEY(uid));
    if (!raw) return emptyModel(age);
    const m = JSON.parse(raw);
    if (!m.ability) m.ability = emptyModel(age).ability;
    // backfill ability for any new world added later
    WORLDS.forEach((w) => { if (m.ability[w.id] == null) m.ability[w.id] = abilityFromAge(m.age || age); });
    return m;
  } catch { return emptyModel(age); }
}
function write(uid, m) { try { localStorage.setItem(KEY(uid), JSON.stringify(m)); } catch {} }

export function useLearnerModel() {
  const profile = useAuthStore((s) => s.user?.studentProfile);
  const uid = useAuthStore((s) => s.user?.id);

  // resolve age: backend age → grade-derived → stored local → default
  const resolveAge = useCallback(() => {
    // a user-set override (Flow Settings) always wins, so changing age takes effect immediately
    try { const m = JSON.parse(localStorage.getItem(KEY(uid)) || "null"); if (m?.ageSet && m?.age) return m.age; } catch {}
    if (profile?.age && profile.age >= 5 && profile.age <= 19) return profile.age;
    const g = parseInt(String(profile?.grade || "").match(/\d+/)?.[0] || "", 10);
    if (g >= 1 && g <= 13) return g + 6;
    return null;
  }, [profile?.age, profile?.grade, uid]);

  const [model, setModel] = useState(() => read(uid, resolveAge() || 12));
  const [needsAge, setNeedsAge] = useState(() => resolveAge() == null);

  useEffect(() => {
    const a = resolveAge();
    setModel(read(uid, a || 12));
    setNeedsAge(a == null);
  }, [uid, resolveAge]);

  const setAge = useCallback((age) => {
    setModel((prev) => {
      const next = { ...prev, age, ageSet: true, ability: { ...prev.ability } };
      // nudge any still-default abilities toward the new age baseline
      WORLDS.forEach((w) => { if (next.ability[w.id] == null) next.ability[w.id] = abilityFromAge(age); });
      write(uid, next); return next;
    });
    setNeedsAge(false);
  }, [uid]);

  /** Record a finished module: ELO ability update + spaced-repetition scheduling. */
  const recordOutcome = useCallback((module, correct, total) => {
    setModel((prev) => {
      const next = { ...prev, ability: { ...prev.ability }, items: { ...prev.items } };
      const rating = moduleRating(module);
      const A = next.ability[module.world] ?? abilityFromAge(next.age || 12);
      const actual = total ? correct / total : 0;
      const expected = 1 / (1 + Math.pow(10, (rating - A) / 25)); // logistic
      const K = 9;
      next.ability[module.world] = Math.max(0, Math.min(100, Math.round(A + K * (actual - expected))));

      // SM-2-lite scheduling
      const it = prev.items[module.id] || { reps: 0, ease: 2.3, intervalD: 0 };
      const stars = actual >= 1 ? 3 : actual >= 0.75 ? 2 : actual >= 0.5 ? 1 : 0;
      let { reps, ease, intervalD } = it;
      if (stars >= 2) {
        reps += 1;
        ease = Math.max(1.3, ease + (stars === 3 ? 0.12 : -0.02));
        intervalD = reps === 1 ? 1 : reps === 2 ? 3 : Math.round((intervalD || 3) * ease);
      } else {
        reps = 0; ease = Math.max(1.3, ease - 0.2); intervalD = 0; // relearn soon
      }
      const dueAt = stars >= 2 ? now() + intervalD * DAY : now() + 10 * 60000;
      next.items[module.id] = { reps, ease, intervalD, dueAt, lastStars: Math.max(stars, it.lastStars || 0), lastTs: now(), seen: true };

      write(uid, next); return next;
    });
  }, [uid]);

  /** Score one module for THIS learner (higher = surface sooner). */
  const scoreModule = useCallback((m, mdl) => {
    const A = mdl.ability[m.world] ?? abilityFromAge(mdl.age || 12);
    const rating = moduleRating(m);
    const it = mdl.items[m.id];
    // optimal challenge = slightly above current ability (ZPD)
    let s = 100 - Math.abs(rating - (A + 8));
    // age fit: penalise content far from the learner's age band
    const [lo, hi] = (() => { const n = ageNums(m.age); return [n[0] ?? 8, n[1] ?? n[0] ?? 16]; })();
    const age = mdl.age || 12;
    if (age < lo - 1) s -= (lo - age) * 8;
    if (age > hi + 2) s -= (age - hi) * 5;
    // spaced repetition: due reviews jump to the top
    if (it?.seen && it.dueAt && it.dueAt <= now()) s += 65;
    // novelty for unseen
    if (!it?.seen) s += 16;
    // de-prioritise freshly mastered, not-yet-due
    if (it?.lastStars === 3 && it.dueAt && it.dueAt > now()) s -= 45;
    return s;
  }, []);

  /** Personalised, ranked module list. */
  const recommended = useCallback((limit = 6) => {
    const ranked = MODULES.map((m) => ({ m, s: scoreModule(m, model) })).sort((a, b) => b.s - a.s);
    return ranked.slice(0, limit).map((r) => r.m);
  }, [model, scoreModule]);

  /** Modules due for spaced-repetition review right now. */
  const dueReviews = useCallback(() => MODULES.filter((m) => { const it = model.items[m.id]; return it?.seen && it.dueAt && it.dueAt <= now(); }), [model]);

  /** A friendly per-subject level label (1–10) from ability. */
  const subjectLevel = useCallback((worldId) => Math.max(1, Math.min(10, Math.round((model.ability[worldId] ?? 30) / 10))), [model]);

  /** Adaptive plan for a module: how many tasks + harder ordering for strong learners. */
  const planFor = useCallback((m) => {
    const A = model.ability[m.world] ?? 40;
    const rating = moduleRating(m);
    const gap = A - rating; // >0 = learner stronger than this module
    return {
      strong: gap > 12,         // ace-level → full set, no hand-holding
      struggling: gap < -18,    // serve a focused, shorter set
      level: subjectLevel(m.world),
    };
  }, [model, subjectLevel]);

  return {
    age: model.age, needsAge, setAge,
    ability: model.ability, subjectLevel,
    recommended, dueReviews, recordOutcome, planFor,
    items: model.items,
  };
}
